import React, { useState, useEffect, createContext, useContext } from 'react';
import { CarvanaTheme } from '@carvana/showroom-theme';
import { canIUseApi } from '../../utils/can-i-use-api';
import { isClient } from '../../utils/is-client';
import { throttle } from '../../utils/throttle';

const defaultValue = {};
const BreakpointContext = createContext(defaultValue);

// CarvanaTheme.breakpoint - "@carvana/showroom-theme": "5.1.0"
// xs: 0
// sm: 414
// md: 768
// lg: 1024
// xl: 1280
// xxl: 1440

// named sizes
// mobile: <= 414
// tabletSm: 415 - 768
// tabletMd: 769 - 1024
// laptop: 1024 - 1440
// desktop: > 1440

const mediaQueries = {
  mobile: `(max-width: ${CarvanaTheme.breakpoint.sm}px)`,
  tabletSm: `(min-width: ${CarvanaTheme.breakpoint.sm + 1}px) and (max-width: ${
    CarvanaTheme.breakpoint.md
  }px)`,
  tabletMd: `(min-width: ${CarvanaTheme.breakpoint.md + 1}px) and (max-width: ${
    CarvanaTheme.breakpoint.lg
  }px)`,
  laptop: `(min-width: ${CarvanaTheme.breakpoint.lg + 1}px) and (max-width: ${
    CarvanaTheme.breakpoint.xl
  }px)`,
  desktop: `(min-width: ${CarvanaTheme.breakpoint.xl + 1}px)`,
  portrait: '(orientation: portrait)'
};

export const BreakpointProvider = ({ children }) => {
  const [queryMatch, setQueryMatch] = useState({});

  useEffect(() => {
    const mediaQueryList = {};
    const keys = Object.keys(mediaQueries);

    const handleQueryListener = () => {
      const updatedMatches = keys.reduce((acc, media) => {
        acc[media] = !!(mediaQueryList[media] && mediaQueryList[media].matches);
        return acc;
      }, {});

      setQueryMatch(updatedMatches);
    };

    if (isClient() && canIUseApi('matchMedia')) {
      const throttledHandleQueryListener = throttle(handleQueryListener, 250);
      const matches = {};
      keys.forEach((media) => {
        mediaQueryList[media] = window.matchMedia(mediaQueries[media]);
        matches[media] = mediaQueryList[media].matches;

        mediaQueryList[media].addListener(throttledHandleQueryListener);
      });

      setQueryMatch(matches);

      return () => {
        keys.forEach((media) => {
          mediaQueryList[media].removeListener(throttledHandleQueryListener);
        });
      };
    }
  }, []);

  return (
    <BreakpointContext.Provider
      value={{
        ...queryMatch,
        isMobile: queryMatch.mobile,
        isTablet: queryMatch.tabletSm || queryMatch.tabletMd,
        isDesktop: queryMatch.laptop || queryMatch.desktop,
        isClient: isClient()
      }}
    >
      {children}
    </BreakpointContext.Provider>
  );
};

export const useBreakpoint = () => {
  const context = useContext(BreakpointContext);
  return context;
};
